import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Key, Lock, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import authService from '../services/authService';
import { getErrorMessage } from '../lib/apiError';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password, 4: Success
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Step 1: Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await authService.forgotPassword(email);
      setMessage(response.message || 'If an account exists with this email, an OTP has been sent.');
      setStep(2);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to send OTP. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.verifyResetOTP(email, otp);
      setStep(3);
    } catch (err) {
      setError(getErrorMessage(err, 'Invalid OTP. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      await authService.resetPassword(email, otp, newPassword);
      setStep(4);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to reset password. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await authService.forgotPassword(email);
      setMessage('A new OTP has been sent to your email.');
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen liquid-bg flex items-center justify-center p-4">
      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="liquid-blob blob-1"></div>
        <div className="liquid-blob blob-2"></div>
        <div className="liquid-blob blob-3"></div>
        <div className="liquid-blob blob-4"></div>
      </div>

      <main className="relative z-10 w-full max-w-md" aria-label="Reset password">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-block">
            <img src="/assets/Arantree_logo_upscaled.png" alt="Arantree" className="h-16 mx-auto mb-4" />
          </div>
          <h1 className="text-3xl font-bold gradient-text mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Reset Password
          </h1>
          <p className="text-gray-600">CarbonRoute · Supply Chain Emission Calculator</p>
        </div>

        {/* Main Card */}
        <div className="glass-panel p-8">
          {/* Step Indicators */}
          {step < 4 && (
            <div className="flex justify-center mb-6">
              <div className="flex items-center space-x-2">
                {[1, 2, 3].map((s) => (
                  <React.Fragment key={s}>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                        step >= s
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {s}
                    </div>
                    {s < 3 && (
                      <div
                        className={`w-8 h-0.5 ${
                          step > s ? 'bg-teal-500' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="glass-alert glass-alert-error mb-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Success Message */}
          {message && step === 2 && (
            <div className="glass-alert glass-alert-success mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{message}</span>
            </div>
          )}

          {/* Step 1: Enter Email */}
          {step === 1 && (
            <form onSubmit={handleRequestOTP}>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Enter your email</h2>
              <p className="text-gray-600 text-sm mb-6">
                We'll send you a 6-digit OTP to reset your password.
              </p>

              <div className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="glass-input w-full pl-10 pr-4 py-3 text-gray-900"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="glass-button-primary w-full py-3 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Step 2: Enter OTP */}
          {step === 2 && (
            <form onSubmit={handleVerifyOTP}>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Enter OTP</h2>
              <p className="text-gray-600 text-sm mb-6">
                Enter the 6-digit code sent to <span className="font-medium text-gray-800">{email}</span>
              </p>

              <div className="space-y-4">
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    required
                    maxLength={6}
                    className="glass-input w-full pl-10 pr-4 py-3 text-center text-2xl tracking-widest text-gray-900"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="glass-button-primary w-full py-3 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify OTP'
                  )}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="text-teal-600 hover:text-teal-500 text-sm font-medium disabled:opacity-50"
                  >
                    Resend OTP
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword}>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Create new password</h2>
              <p className="text-gray-600 text-sm mb-6">
                Enter a strong password for your account.
              </p>

              <div className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    required
                    minLength={8}
                    className="glass-input w-full pl-10 pr-4 py-3 text-gray-900"
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    required
                    minLength={8}
                    className="glass-input w-full pl-10 pr-4 py-3 text-gray-900"
                  />
                </div>

                <p className="text-gray-500 text-xs">
                  Password must be at least 8 characters long with a mix of letters, numbers, and special characters.
                </p>

                <button
                  type="submit"
                  disabled={loading}
                  className="glass-button-primary w-full py-3 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
                <CheckCircle className="h-8 w-8 text-teal-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Password Reset Successful!</h2>
              <p className="text-gray-600 text-sm mb-6">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="glass-button-primary w-full py-3"
              >
                Sign In
              </button>
            </div>
          )}

          {/* Back to Login Link */}
          {step < 4 && (
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-500 transition-colors font-medium"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Sign In
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img
              src="/assets/Arantree_logo_upscaled.png"
              alt="Arantree"
              className="h-8 w-auto"
            />
            <div className="text-left">
              <p className="text-xs text-gray-400">Powered by</p>
              <p className="text-sm font-medium text-gray-600">Arantree Consulting Services</p>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Contact: <a href="mailto:info@arantree.com" className="text-teal-600 hover:text-teal-500">info@arantree.com</a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
